
import React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import { log } from '@/utils/logger';
import { parseLocalDate } from '@/utils/dateUtils';
import './HotelDetailView.css';

const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <span className={className}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  </span>
);

const PersonIcon: React.FC<{ className?: string }> = ({ className }) => (
  <span className={className}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  </span>
);

const SearchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <span className={className}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  </span>
);

export interface HotelDetailSearchBarProps {
  editCheckIn: Dayjs | null;
  editCheckOut: Dayjs | null;
  editAdults: number;
  editRooms: number;
  onCheckInChange: (value: Dayjs | null) => void;
  onCheckOutChange: (value: Dayjs | null) => void;
  onRoomsGuestsClick: () => void;
  onChangeSearch: () => void;
  isSearchChanging?: boolean;
  searchChangeError?: string | null;
  variant?: 'header' | 'section';
  locationLabel?: string;
  onWiderSearch?: () => void;
  isWiderSearchLoading?: boolean;
  hasNoRooms?: boolean; // When true: label becomes "Change dates" and "Search nearby hotels" is shown
}

const HotelDetailSearchBar: React.FC<HotelDetailSearchBarProps> = ({
  editCheckIn,
  editCheckOut,
  editAdults,
  editRooms,
  onCheckInChange,
  onCheckOutChange,
  onRoomsGuestsClick,
  onChangeSearch,
  isSearchChanging = false,
  variant = 'section',
  locationLabel,
  onWiderSearch,
  isWiderSearchLoading = false,
  hasNoRooms = false,
}) => {
  const [checkInOpen, setCheckInOpen] = React.useState(false);
  const [checkOutOpen, setCheckOutOpen] = React.useState(false);

  const handleCheckInChange = (date: Dayjs | null) => {
    if (date) {
      const localDate = parseLocalDate(date.toDate());
      onCheckInChange(localDate ? dayjs(localDate) : null);
      setCheckInOpen(false);
    } else {
      onCheckInChange(null);
    }
  };

  const handleCheckOutChange = (date: Dayjs | null) => {
    if (date) {
      const localDate = parseLocalDate(date.toDate());
      onCheckOutChange(localDate ? dayjs(localDate) : null);
      setCheckOutOpen(false);
    } else {
      onCheckOutChange(null);
    }
  };

  const handleChangeSearchClick = () => {
    log('[HotelDetailSearchBar] Change search clicked', { variant });
    onChangeSearch();
  };

  const canChangeSearch = editCheckIn != null && editCheckOut != null && editCheckOut.isAfter(editCheckIn);

  // Header pill variant
  if (variant === 'header') {
    const checkInStr = editCheckIn ? editCheckIn.format('MMM DD') : 'Select';
    const checkOutStr = editCheckOut ? editCheckOut.format('MMM DD') : 'Select';
    const guestsStr = `${editAdults} Guest${editAdults !== 1 ? 's' : ''}`;

    return (
      <div className="hotel-detail-search-pill-wrapper">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className="hotel-detail-search-pill">
            {/* Location segment */}
            <div className="hotel-detail-search-pill-segment">
              <span className="hotel-detail-search-pill-value">{locationLabel || '—'}</span>
            </div>

            <div className="hotel-detail-search-pill-divider" />

            {/* Check-in segment */}
            <div
              className="hotel-detail-search-pill-segment hotel-detail-search-pill-segment--clickable"
              onClick={() => setCheckInOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setCheckInOpen(true)}
            >
              <span className="hotel-detail-search-pill-value">{checkInStr}</span>
              <div onClick={(e) => e.stopPropagation()}>
                <DatePicker
                  value={editCheckIn}
                  onChange={handleCheckInChange}
                  minDate={dayjs()}
                  closeOnSelect={true}
                  format="DD MMM"
                  open={checkInOpen}
                  onOpen={() => setCheckInOpen(true)}
                  onClose={() => setCheckInOpen(false)}
                  slots={{ openPickerIcon: () => null }}
                  slotProps={{
                    textField: {
                      sx: {
                        position: 'absolute',
                        opacity: 0,
                        width: 1,
                        height: 1,
                        overflow: 'hidden',
                        clip: 'rect(0, 0, 0, 0)',
                        whiteSpace: 'nowrap',
                      },
                    },
                    popper: { style: { zIndex: 10000000 }, placement: 'bottom-start' },
                    dialog: { style: { zIndex: 10000000 } },
                    mobilePaper: { style: { zIndex: 10000000 } },
                    actionBar: { actions: [] },
                  }}
                />
              </div>
            </div>

            <div className="hotel-detail-search-pill-divider" />

            {/* Check-out segment */}
            <div
              className="hotel-detail-search-pill-segment hotel-detail-search-pill-segment--clickable"
              onClick={() => setCheckOutOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setCheckOutOpen(true)}
            >
              <span className="hotel-detail-search-pill-value">{checkOutStr}</span>
              <div onClick={(e) => e.stopPropagation()}>
                <DatePicker
                  value={editCheckOut}
                  onChange={handleCheckOutChange}
                  minDate={editCheckIn ? editCheckIn.add(1, 'day') : dayjs().add(1, 'day')}
                  closeOnSelect={true}
                  format="DD MMM"
                  open={checkOutOpen}
                  onOpen={() => setCheckOutOpen(true)}
                  onClose={() => setCheckOutOpen(false)}
                  slots={{ openPickerIcon: () => null }}
                  slotProps={{
                    textField: {
                      sx: {
                        position: 'absolute',
                        opacity: 0,
                        width: 1,
                        height: 1,
                        overflow: 'hidden',
                        clip: 'rect(0, 0, 0, 0)',
                        whiteSpace: 'nowrap',
                      },
                    },
                    popper: { style: { zIndex: 10000000 }, placement: 'bottom-start' },
                    dialog: { style: { zIndex: 10000000 } },
                    mobilePaper: { style: { zIndex: 10000000 } },
                    actionBar: { actions: [] },
                  }}
                />
              </div>
            </div>

            <div className="hotel-detail-search-pill-divider" />

            {/* Guests segment */}
            <div
              className="hotel-detail-search-pill-segment hotel-detail-search-pill-segment--clickable"
              onClick={onRoomsGuestsClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && onRoomsGuestsClick()}
            >
              <span className="hotel-detail-search-pill-value">{guestsStr}</span>
            </div>

            {/* Search button — opens whitelabel listing for the hotel's location */}
            <button
              type="button"
              className="hotel-detail-search-pill-btn"
              onClick={isWiderSearchLoading ? undefined : () => onWiderSearch?.()}
              disabled={isWiderSearchLoading}
              aria-label="Search hotels in this area"
            >
              <SearchIcon className="hotel-detail-search-pill-icon" />
            </button>
          </div>
        </LocalizationProvider>
      </div>
    );
  }

  // Section variant (unchanged) - at this point variant is always 'section'
  return (
    <div className="hotel-detail-search-row">
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div className="hotel-detail-search-field">
          <DatePicker
            value={editCheckIn}
            onChange={handleCheckInChange}
            minDate={dayjs()}
            closeOnSelect={true}
            format="DD MMM"
            open={checkInOpen}
            onOpen={() => setCheckInOpen(true)}
            onClose={() => setCheckInOpen(false)}
            slots={{
              openPickerIcon: () => null,
            }}
            slotProps={{
              textField: {
                placeholder: 'Check-in',
                size: 'small',
                onClick: () => setCheckInOpen(true),
                InputProps: {
                  readOnly: true,
                  startAdornment: <CalendarIcon className="hotel-detail-search-field-icon" />,
                  sx: { cursor: 'pointer' },
                },
                sx: {
                  '& .MuiInputBase-input': {
                    cursor: 'pointer',
                  },
                },
              },
              popper: {
                style: { zIndex: 10000000 },
                placement: 'bottom-start',
              },
              dialog: {
                style: { zIndex: 10000000 },
              },
              mobilePaper: {
                style: { zIndex: 10000000 },
              },
              actionBar: {
                actions: [],
              },
            }}
          />
        </div>

        <div className="hotel-detail-search-field">
          <DatePicker
            value={editCheckOut}
            onChange={handleCheckOutChange}
            minDate={editCheckIn ? editCheckIn.add(1, 'day') : dayjs().add(1, 'day')}
            closeOnSelect={true}
            format="DD MMM"
            open={checkOutOpen}
            onOpen={() => setCheckOutOpen(true)}
            onClose={() => setCheckOutOpen(false)}
            slots={{
              openPickerIcon: () => null,
            }}
            slotProps={{
              textField: {
                placeholder: 'Check-out',
                size: 'small',
                onClick: () => setCheckOutOpen(true),
                InputProps: {
                  readOnly: true,
                  startAdornment: <CalendarIcon className="hotel-detail-search-field-icon" />,
                  sx: { cursor: 'pointer' },
                },
                sx: {
                  '& .MuiInputBase-input': {
                    cursor: 'pointer',
                  },
                },
              },
              popper: {
                style: { zIndex: 10000000 },
                placement: 'bottom-start',
              },
              dialog: {
                style: { zIndex: 10000000 },
              },
              mobilePaper: {
                style: { zIndex: 10000000 },
              },
              actionBar: {
                actions: [],
              },
            }}
          />
        </div>
      </LocalizationProvider>

      <button
        type="button"
        className="hotel-detail-search-field hotel-detail-search-field-button"
        onClick={onRoomsGuestsClick}
      >
        <PersonIcon className="hotel-detail-search-field-icon" />
        <span className="hotel-detail-search-field-text">
          {editRooms} Room{editRooms !== 1 ? 's' : ''}, {editAdults} Adult{editAdults !== 1 ? 's' : ''}
        </span>
      </button>

      <button
        type="button"
        className="hotel-detail-change-search-btn"
        disabled={isSearchChanging || !canChangeSearch}
        onClick={handleChangeSearchClick}
      >
        {isSearchChanging ? (
          <span className="hotel-detail-change-search-spinner">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="32"
                strokeDashoffset="32"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  dur="2s"
                  values="32;0"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </span>
        ) : (
          <SearchIcon className="hotel-detail-change-search-icon" />
        )}
        <span>{hasNoRooms ? 'Change dates' : 'Change search'}</span>
      </button>

      {hasNoRooms && onWiderSearch && (
        <button
          type="button"
          className="hotel-detail-nearby-search-btn"
          onClick={isWiderSearchLoading ? undefined : () => onWiderSearch()}
          disabled={isWiderSearchLoading}
        >
          <SearchIcon className="hotel-detail-change-search-icon" />
          <span>{isWiderSearchLoading ? 'Please wait...' : 'Search nearby hotels'}</span>
        </button>
      )}
    </div>
  );
};

export default HotelDetailSearchBar;
